import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  type TriggerFn,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import type { TextNode } from 'lexical';
import { $createTextNode, $getSelection, $isRangeSelection } from 'lexical';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Command, CommandGroup, CommandItem, CommandList } from '../../ui/command';
import { AutocompleteNode } from '../nodes/autocomplete-node';

const SUGGESTION_LIST_LENGTH_LIMIT = 5;

interface Variable {
  name: string;
  label: string;
}

interface VariableAutocompletePluginProps {
  variables: Variable[];
  isInDialog?: boolean;
}

class VariableOption extends MenuOption {
  name: string;
  label: string;

  constructor(name: string, label: string) {
    super(name);
    this.name = name;
    this.label = label;
  }
}

export function VariableAutocompletePlugin({
  variables,
  isInDialog = false,
}: VariableAutocompletePluginProps): React.ReactElement | null {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState<string | null>(null);
  const [menuParent, setMenuParent] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!editor.hasNodes([AutocompleteNode])) {
      throw new Error('VariableAutocompletePlugin: AutocompleteNode not registered on editor');
    }

    if (!isInDialog) {
      setMenuParent(null);
      return;
    }

    return editor.registerRootListener((nextRoot) => {
      if (nextRoot) {
        setMenuParent(nextRoot.parentElement ?? nextRoot);
      } else {
        setMenuParent(null);
      }
    });
  }, [editor, isInDialog]);

  // Custom trigger function that matches { or {{ pattern
  const checkForTriggerMatch: TriggerFn = useCallback((text: string) => {
    // First try to match {{ pattern (preferred)
    let match = text.match(/\{\{([^}]*)$/);

    if (match && match.index !== undefined) {
      return {
        leadOffset: match.index,
        matchingString: match[1], // Everything after {{
        replaceableString: match[0],
      };
    }

    // If no {{ match, try single { pattern
    match = text.match(/\{([^}]*)$/);

    if (match && match.index !== undefined) {
      return {
        leadOffset: match.index,
        matchingString: match[1], // Everything after {
        replaceableString: match[0],
      };
    }

    return null;
  }, []);

  const options = useMemo(() => {
    if (queryString === null || queryString === undefined) {
      return [];
    }

    // Handle {{ pattern (queryString will be '{')
    if (queryString === '{') {
      return variables
        .slice(0, SUGGESTION_LIST_LENGTH_LIMIT)
        .map((variable) => new VariableOption(variable.name, variable.label));
    }

    // Handle {{text pattern (e.g., {{n → queryString = '{n')
    if (queryString.startsWith('{')) {
      const actualQuery = queryString.substring(1);
      const lowerQuery = actualQuery.toLowerCase();
      return variables
        .filter(
          (variable) =>
            variable.name.toLowerCase().startsWith(lowerQuery) ||
            variable.label.toLowerCase().includes(lowerQuery)
        )
        .slice(0, SUGGESTION_LIST_LENGTH_LIMIT)
        .map((variable) => new VariableOption(variable.name, variable.label));
    }

    // Handle single { pattern
    if (queryString === '') {
      return variables
        .slice(0, SUGGESTION_LIST_LENGTH_LIMIT)
        .map((variable) => new VariableOption(variable.name, variable.label));
    }

    // Handle {text pattern (e.g., {n → queryString = 'n')
    const lowerQuery = queryString.toLowerCase();
    return variables
      .filter(
        (variable) =>
          variable.name.toLowerCase().startsWith(lowerQuery) ||
          variable.label.toLowerCase().includes(lowerQuery)
      )
      .slice(0, SUGGESTION_LIST_LENGTH_LIMIT)
      .map((variable) => new VariableOption(variable.name, variable.label));
  }, [queryString, variables]);

  const onSelectOption = useCallback(
    (selectedOption: VariableOption, nodeToRemove: TextNode | null, closeMenu: () => void) => {
      const selection = $getSelection();

      if (!$isRangeSelection(selection) || selectedOption == null) {
        return;
      }

      if (nodeToRemove) {
        nodeToRemove.remove();
      }

      const completion = `{{${selectedOption.name}}} `;
      const textNode = $createTextNode(completion);

      selection.insertNodes([textNode]);
      textNode.selectNext();

      closeMenu();
    },
    []
  );

  return (
    <LexicalTypeaheadMenuPlugin<VariableOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={checkForTriggerMatch}
      options={options}
      parent={isInDialog ? (menuParent ?? undefined) : undefined}
      menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp }) => {
        if (anchorElementRef.current == null) {
          return null;
        }

        if (isInDialog && menuParent) {
          const rect = menuParent.getBoundingClientRect();
          anchorElementRef.current.style.transform = `translate(${-rect.left}px, ${-rect.top}px)`;
        } else {
          anchorElementRef.current.style.transform = '';
        }

        return createPortal(
          <div
            className="z-[9999] w-[300px] rounded-md shadow-lg border bg-popover"
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '4px',
            }}
          >
            <Command>
              <CommandList>
                <CommandGroup heading="Variables">
                  {options.length === 0 ? (
                    <CommandItem disabled className="text-muted-foreground text-xs !bg-transparent">
                      No variables defined yet. Add one using the "Add Variable" button.
                    </CommandItem>
                  ) : (
                    options.map((option, index) => (
                      <CommandItem
                        key={option.key}
                        value={option.name}
                        onSelect={() => {
                          selectOptionAndCleanUp(option);
                        }}
                        className={`flex items-center gap-2 ${
                          selectedIndex === index ? 'bg-accent' : '!bg-transparent'
                        }`}
                      >
                        <div className="flex flex-col">
                          <span className="font-mono text-sm">{`{{${option.name}}}`}</span>
                          <span className="text-xs text-muted-foreground">{option.label}</span>
                        </div>
                      </CommandItem>
                    ))
                  )}
                </CommandGroup>
              </CommandList>
            </Command>
          </div>,
          anchorElementRef.current
        );
      }}
    />
  );
}
